import { DynamicFormRequiredType, type DynamicFormSettings, type VisibilityCondition } from '@myosh/odin-components';
import i18next from 'i18next';
import type { CombinedFieldType } from '../../../../@types/forms';
import type { FormBuilderField, FormBuilderFormGroup } from '../../../../redux/slices/form-builder-slice';

const attachDocVisibilityCondition: VisibilityCondition = {
  id: 'attachDocVisibilityCondition',
  sourceFieldId: 'attachGeneratedDoc',
  fieldVisibilityType: 'FIELD',
  targetValueType: 'BOOLEAN',
  targetValue: true,
  negation: false,
};

export const emailThisButtonFormSettings: DynamicFormSettings = {
  id: 'EmailThisButtonGroup',
  caption: '',
  fields: [
    {
      id: 'keepEmailAudit',
      fieldType: 'CHECKBOX',
      fieldName: 'keepEmailAudit',
      caption: i18next.t('keep-audit-of-emails-sent'),
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 35,
    },
    {
      id: 'includeAttachment',
      fieldType: 'CHECKBOX',
      fieldName: 'includeAttachment',
      caption: i18next.t('include-attachment'),
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 40,
    },
    {
      id: 'attachGeneratedDoc',
      fieldType: 'CHECKBOX',
      fieldName: 'attachGeneratedDoc',
      caption: i18next.t('attach-generated-doc'),
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 45,
    },
    {
      id: 'generateDocFieldId',
      fieldType: 'COMBOBOX',
      fieldName: 'generateDocFieldId',
      caption: i18next.t('generate-doc-field'),
      permissions: { edit: true, read: true },
      position: 50,
      dataSettings: {
        textField: 'text',
        valueField: 'value',
      },
      customFieldType: 'COMBOBOX',
    },
    {
      id: 'buttonCaption',
      fieldType: 'TEXTFIELD',
      fieldName: 'buttonCaption',
      caption: i18next.t('button-caption'),
      required: DynamicFormRequiredType.True,
      position: 6,
      permissions: { edit: true, read: true },
    },
    {
      id: 'emailThisDefaultEmailSubject',
      fieldType: 'CUSTOM',
      customFieldType: 'TEXTFIELD',
      fieldName: 'emailThisDefaultEmailSubject',
      caption: i18next.t('default-subject'),
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 55,
    },
    {
      id: 'emailThisDefaultEmailBody',
      fieldType: 'CUSTOM',
      customFieldType: 'TEXTAREA',
      fieldName: 'emailThisDefaultEmailBody',
      caption: i18next.t('default-body'),
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 60,
      dynamicProperties: {
        rows: 8,
      },
    },
  ],
  visibilityConditions: [
    {
      id: 'generateDocFieldId',
      type: 'COMBOBOX',
      groups: [
        {
          conditions: [attachDocVisibilityCondition],
        },
      ],
    },
  ],
};

export const getDocFieldOptions = (fieldGroups: FormBuilderFormGroup[]) => {
  return (
    fieldGroups
      ?.flatMap((group) =>
        group.fields
          ?.filter((field) => ['PDF_EXPORT', 'WORD_EXPORT'].includes(field.fieldType as CombinedFieldType))
          ?.map((field) => ({
            text: field.buttonCaption?.translations[0].value,
            value: field.id,
          }))
      )
      ?.filter(Boolean) || []
  );
};

export const getEmailThisButtonData = (field: FormBuilderField, fieldGroups: FormBuilderFormGroup[]) => {
  const generateDocFieldOptions = getDocFieldOptions(fieldGroups);

  return {
    buttonCaption: field.buttonCaption?.translations[0]?.value ?? '',
    keepEmailAudit: field.keepEmailAudit ?? false,
    includeAttachment: field.includeAttachment ?? false,
    attachGeneratedDoc: field.attachGeneratedDoc ?? false,
    generateDocFieldId: generateDocFieldOptions.find((opt) => opt.value === field.generateDocFieldId) ?? '',
    emailThisDefaultEmailSubject: field.emailThisDefaultEmailSubject ?? '',
    emailThisDefaultEmailBody: field.emailThisDefaultEmailBody ?? '',
  };
};
